import { useEffect, useState } from "react";
import { Save, Upload, Palette, Globe, Share2, Paintbrush } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fileUrl } from "../../lib/api";
import { PageHeader, LoadingRows } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const F = (b, setB, key, label, props = {}) => (
  <div className="space-y-1.5" key={key}>
    <Label className="overline-label">{label}</Label>
    <Input data-testid={`branding-${key}`} value={b[key] || ""} onChange={(e) => setB({ ...b, [key]: e.target.value })} {...props} />
  </div>
);

export default function PartnerBranding() {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/partner/branding").then((r) => setB(r.data.branding || {})); }, []);
  if (!b) return <LoadingRows rows={4} />;

  const upload = async (file, key) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd);
      setB((x) => ({ ...x, [key]: data.url }));
      toast.success("Uploaded");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const save = async () => {
    setBusy(true);
    try { await api.put("/partner/branding", b); toast.success("Branding saved — your client websites now use your brand"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const UploadBtn = ({ k, label }) => (
    <label className="h-10 px-4 rounded-md border border-border text-xs font-bold flex items-center gap-1.5 cursor-pointer hover:bg-muted w-fit" data-testid={`branding-upload-${k}`}>
      <Upload className="h-3.5 w-3.5" /> {label}
      <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f, k); e.target.value = ""; }} />
    </label>
  );

  return (
    <div data-testid="partner-branding">
      <PageHeader title="White-Label Branding" subtitle="Your brand appears on every client website — MILTROO stays invisible.">
        <Button data-testid="branding-save" onClick={save} disabled={busy} className="rounded-full font-bold active:scale-95 transition-transform">
          <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save Branding"}
        </Button>
      </PageHeader>

      <div className="border border-emerald-500/20 bg-emerald-500/5 rounded-md p-5 mb-8 max-w-3xl flex gap-3">
        <Paintbrush className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
        <p className="text-sm text-muted-foreground leading-relaxed">
          Business Owner websites automatically inherit this branding: your brand name, logo, favicon, colors, contact details,
          footer credit and social links. Client stores show <span className="font-bold text-foreground">your agency's brand</span> — never the platform's.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 max-w-5xl">
        <div className="bg-card border border-border rounded-md p-8 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center"><Palette className="h-4 w-4" /></div>
            <p className="font-display font-bold text-lg">Brand Identity</p>
          </div>
          {F(b, setB, "brand_name", "Brand name", { placeholder: "e.g. Acme Web Studio" })}
          {F(b, setB, "tagline", "Tagline", { placeholder: "Websites that win clients" })}
          {F(b, setB, "website_title", "Default website title (browser tab)", { placeholder: "Acme Web Studio" })}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="overline-label">Logo</Label>
              {b.logo_url && <img src={fileUrl(b.logo_url)} alt="logo" className="h-12 object-contain rounded-md border border-border p-1.5 bg-white" data-testid="branding-logo-preview" />}
              <UploadBtn k="logo_url" label="Upload logo" />
              <Input data-testid="branding-logo_url" value={b.logo_url || ""} onChange={(e) => setB({ ...b, logo_url: e.target.value })} placeholder="or paste image URL" className="text-xs" />
            </div>
            <div className="space-y-1.5">
              <Label className="overline-label">Favicon</Label>
              {b.favicon_url && <img src={fileUrl(b.favicon_url)} alt="favicon" className="h-8 w-8 object-contain rounded-md border border-border p-1 bg-white" data-testid="branding-favicon-preview" />}
              <UploadBtn k="favicon_url" label="Upload favicon" />
              <Input data-testid="branding-favicon_url" value={b.favicon_url || ""} onChange={(e) => setB({ ...b, favicon_url: e.target.value })} placeholder="or paste image URL" className="text-xs" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="text-xs font-bold">Brand primary color
              <input type="color" data-testid="branding-primary_color" value={b.primary_color || "#1E3A8A"} onChange={(e) => setB({ ...b, primary_color: e.target.value })} className="block w-full h-10 mt-1 rounded-md border border-border cursor-pointer" />
            </label>
            <label className="text-xs font-bold">Brand accent color
              <input type="color" data-testid="branding-accent_color" value={b.accent_color || "#F59E0B"} onChange={(e) => setB({ ...b, accent_color: e.target.value })} className="block w-full h-10 mt-1 rounded-md border border-border cursor-pointer" />
            </label>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-card border border-border rounded-md p-8 space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center"><Globe className="h-4 w-4" /></div>
              <p className="font-display font-bold text-lg">Domain & Contact</p>
            </div>
            {F(b, setB, "domain", "Your primary domain (for client subdomains)", { placeholder: "myagency.com" })}
            <p className="text-xs text-muted-foreground -mt-2">Client sites without a custom domain get <span className="font-mono font-bold">store-name.{b.domain || "myagency.com"}</span></p>
            {F(b, setB, "contact_email", "Contact email")}
            {F(b, setB, "contact_phone", "Contact phone")}
            {F(b, setB, "footer_text", "Footer credit text", { placeholder: `Powered by ${b.brand_name || "Your Agency"}` })}
          </div>

          <div className="bg-card border border-border rounded-md p-8 space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center"><Share2 className="h-4 w-4" /></div>
              <p className="font-display font-bold text-lg">Social Links</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {F(b, setB, "social_instagram", "Instagram", { placeholder: "@youragency" })}
              {F(b, setB, "social_facebook", "Facebook")}
              {F(b, setB, "social_twitter", "Twitter / X")}
              {F(b, setB, "social_linkedin", "LinkedIn")}
              {F(b, setB, "social_youtube", "YouTube")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
